import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  ArrowLeft, MessageSquare, FileText, Package, Upload, Trash2, Download,
  Plus, Loader2, FolderOpen, Code, Image as ImageIcon, File, Sparkles,
  Send, Paperclip, X, ChevronRight, Play, Archive, GitBranch, GitPullRequest, ExternalLink, CheckCircle, Rocket, Settings as SettingsIcon
} from 'lucide-react';
import { toast } from 'sonner';
import MarkdownRenderer from '../components/MarkdownRenderer';
import * as api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectDetailPage() {
  const { projectId, tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tab || 'chat';
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const p = await api.getProject(projectId);
      setProject(p);
    } catch (e) {
      toast.error('Progetto non trovato');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
      <Loader2 className="animate-spin text-white/40" size={24} />
    </div>
  );

  if (!project) return null;

  const handleExportZip = () => {
    const token = localStorage.getItem('toyou_token');
    // Open in new tab or use fetch + blob
    const url = `${API_URL}/api/projects/${projectId}/export.zip`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${project.name.replace(/\s+/g, '_')}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Errore nel download'));
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C]" data-testid="project-detail-page">
      {/* Top bar */}
      <div className="h-12 sm:h-14 flex items-center px-3 sm:px-6 border-b border-white/[0.06] bg-[#101013]">
        <Link to="/projects" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mr-3 sm:mr-4">
          <ArrowLeft size={16} />
        </Link>
        <FolderOpen size={14} className="text-white/40 mr-1.5 hidden sm:block" />
        <span className="text-white font-semibold truncate mr-2 sm:mr-4 text-sm sm:text-base">{project.name}</span>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}/dev/ide`)} 
          className="text-white/50 hover:text-white hover:bg-white/[0.06] mr-2" data-testid="open-dev-env-button">
          <Code size={14} className="mr-1.5" /> Ambiente Dev
        </Button>
        <Button variant="ghost" size="sm" onClick={handleExportZip} className="text-white/40 hover:text-white hover:bg-white/[0.06]" data-testid="export-zip-button">
          <Download size={14} className="mr-1" /> Esporta ZIP
        </Button>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6 overflow-hidden">
        <Tabs value={activeTab} onValueChange={(v) => navigate(`/projects/${projectId}/${v}`)}>
          <div className="overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white/[0.04] border border-white/[0.06] mb-4 sm:mb-6 inline-flex w-auto min-w-full sm:min-w-0">
            <TabsTrigger value="chat" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <MessageSquare size={14} className="mr-2" /> Chat
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <FileText size={14} className="mr-2" /> Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="artifacts" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <Package size={14} className="mr-2" /> Artifacts
            </TabsTrigger>
            <TabsTrigger value="generate" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <Sparkles size={14} className="mr-2" /> Genera App
            </TabsTrigger>
            <TabsTrigger value="git" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <GitBranch size={14} className="mr-2" /> Git
            </TabsTrigger>
            <TabsTrigger value="deploy" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <Rocket size={14} className="mr-2" /> Deploy
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="chat">
            <ProjectChatTab projectId={projectId} project={project} />
          </TabsContent>
          <TabsContent value="knowledge">
            <KnowledgeBaseTab projectId={projectId} />
          </TabsContent>
          <TabsContent value="artifacts">
            <ArtifactsTab projectId={projectId} />
          </TabsContent>
          <TabsContent value="generate">
            <GenerateAppTab projectId={projectId} onGenerated={() => navigate(`/projects/${projectId}/artifacts`)} />
          </TabsContent>
          <TabsContent value="git">
            <GitTab projectId={projectId} />
          </TabsContent>
          <TabsContent value="deploy">
            <DeployTab projectId={projectId} project={project} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ======== PROJECT CHAT TAB ========
function ProjectChatTab({ projectId, project }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.getModels().then(m => setModels(m.filter(x => x.type === 'chat'))).catch(console.error);
    loadConversations();
  }, [projectId]);

  const loadConversations = async () => {
    try {
      const convs = await api.listProjectConversations(projectId);
      setConversations(convs);
      if (convs.length > 0 && !selectedConv) {
        selectConversation(convs[0].id);
      }
    } catch (e) { console.error(e); }
  };

  const selectConversation = async (convId) => {
    try {
      const conv = await api.getConversation(convId);
      setSelectedConv(conv);
      setMessages(conv.messages || []);
      if (conv.model) setSelectedModel(conv.model);
    } catch (e) { console.error(e); }
  };

  const handleNewChat = async () => {
    try {
      const conv = await api.createProjectConversation(projectId);
      await loadConversations();
      selectConversation(conv.id);
    } catch (e) { toast.error('Errore'); }
  };

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;
    let convId = selectedConv?.id;
    if (!convId) {
      const conv = await api.createProjectConversation(projectId);
      convId = conv.id;
      await loadConversations();
    }
    const text = inputText.trim();
    setInputText('');
    setIsLoading(true);
    const tempMsg = { id: 'temp-' + Date.now(), role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);
    try {
      const result = await api.sendMessage(convId, text, selectedModel);
      setMessages(prev => [...prev.filter(m => m.id !== tempMsg.id), result.user_message, result.assistant_message]);
      await loadConversations();
      
      // Auto-save code blocks as artifacts in the project
      const content = result.assistant_message?.content || '';
      const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
      let match;
      let savedCount = 0;
      while ((match = codeBlockRegex.exec(content)) !== null) {
        const lang = match[1] || 'text';
        const code = match[2].trim();
        if (code.length > 10) {
          // Try to detect filename from context before the code block
          const beforeBlock = content.substring(0, match.index);
          const filenameMatch = beforeBlock.match(/[`*]?([\w\-]+\.\w+)[`*]?\s*(?:\)|:|\n)*\s*$/);
          const title = filenameMatch ? filenameMatch[1] : `snippet_${savedCount + 1}.${lang === 'javascript' ? 'js' : lang === 'python' ? 'py' : lang === 'html' ? 'html' : lang === 'css' ? 'css' : lang === 'json' ? 'json' : 'txt'}`;
          
          try {
            await api.createArtifact(projectId, {
              title: title,
              type: 'app_file',
              language: lang,
              content: code
            });
            savedCount++;
          } catch(e) { console.error('Auto-save artifact error:', e); }
        }
      }
      if (savedCount > 0) {
        toast.success(`${savedCount} file salvati automaticamente negli Artifacts`);
      }
    } catch (e) {
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      toast.error(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  return (
    <div className="flex flex-col sm:flex-row gap-4" style={{ height: 'calc(100vh - 220px)', minHeight: '400px' }}>
      {/* Conv sidebar */}
      <div className="w-full sm:w-56 flex-shrink-0 bg-[#101013] rounded-xl border border-white/[0.06] flex flex-col max-h-40 sm:max-h-full overflow-hidden">
        <div className="p-3">
          <Button onClick={handleNewChat} className="w-full bg-white text-black hover:bg-white/90 h-9 text-sm" data-testid="project-new-chat">
            <Plus size={14} className="mr-1" /> Nuova chat
          </Button>
        </div>
        <ScrollArea className="flex-1 px-2">
          {conversations.map(c => (
            <button key={c.id} onClick={() => selectConversation(c.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors truncate ${
                selectedConv?.id === c.id ? 'bg-white/[0.08] text-white' : 'text-white/50 hover:bg-white/[0.04]'
              }`}>
              <MessageSquare size={12} className="inline mr-2" />{c.title}
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col bg-[#101013] rounded-xl border border-white/[0.06] min-w-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-3">
          <span className="text-xs text-white/30">IAIA nel contesto di: {project.name}</span>
          <div className="flex-1" />
          <Select value={selectedModel} onValueChange={setSelectedModel}>
            <SelectTrigger className="w-[180px] h-8 bg-white/[0.04] border-white/[0.06] text-white text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141418] border-white/[0.08]">
              {models.map(m => (
                <SelectItem key={m.key} value={m.key} className="text-white focus:bg-white/[0.06] focus:text-white text-xs">{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ScrollArea className="flex-1 px-4 py-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-white/30 text-sm">
              <span className="text-2xl mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <span className="font-medium text-white/15">IA</span><span className="font-extrabold text-white/15">IA</span>
              </span>
              <span>Chiedimi qualcosa sul progetto...</span>
            </div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className={`flex gap-3 mb-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs ${
                  msg.role === 'user' ? 'bg-white text-black font-semibold' : 'bg-white/10'
                }`}>
                  {msg.role === 'user' ? 'U' : (
                    <span className="text-[9px] text-white/70" style={{ fontFamily: 'Montserrat' }}>
                      <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
                    </span>
                  )}
                </div>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm overflow-hidden ${
                  msg.role === 'user' ? 'bg-white/[0.08] rounded-tr-sm text-white' : 'bg-[#0B0B0C] border border-white/[0.06] rounded-tl-sm text-white/90'
                }`}>
                  <div className="chat-markdown"><MarkdownRenderer content={msg.content || ''} /></div>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex gap-3 mb-4">
              <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-[9px] text-white/70" style={{ fontFamily: 'Montserrat' }}>
                  <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
                </span>
              </div>
              <div className="bg-[#0B0B0C] border border-white/[0.06] rounded-2xl rounded-tl-sm px-4 py-2.5">
                <span className="text-xs text-white/40">IAIA sta pensando...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </ScrollArea>

        <div className="px-4 py-3 border-t border-white/[0.06]">
          <div className="flex gap-2">
            <Textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Chiedi a IAIA..."
              className="min-h-[40px] max-h-[120px] bg-white/[0.04] border-white/[0.06] text-white text-sm resize-none placeholder:text-white/30"
              rows={1}
              data-testid="project-chat-input"
            />
            <Button onClick={handleSend} disabled={isLoading || !inputText.trim()} className="h-10 w-10 bg-white text-black hover:bg-white/90 flex-shrink-0" data-testid="project-chat-send">
              {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ======== KNOWLEDGE BASE TAB ========
function KnowledgeBaseTab({ projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadFiles(); }, [projectId]);

  const loadFiles = async () => {
    try {
      const data = await api.listKnowledgeFiles(projectId);
      setFiles(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setUploading(true);
    try {
      await api.uploadKnowledgeFile(projectId, file);
      toast.success('File caricato');
      loadFiles();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (fileId) => {
    try {
      await api.deleteKnowledgeFile(projectId, fileId);
      toast.success('File rimosso');
      loadFiles();
    } catch (e) { toast.error(e.message); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Knowledge Base</h2>
          <p className="text-sm text-white/40 mt-1">I documenti caricati saranno usati da IAIA come contesto nelle chat di questo progetto</p>
        </div>
        <div>
          <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden"
            accept=".pdf,.txt,.csv,.md,.json,.html" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="bg-white text-black hover:bg-white/90" data-testid="upload-kb-button">
            {uploading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
            Carica documento
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/40" size={24} /></div>
      ) : files.length === 0 ? (
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardContent className="flex flex-col items-center py-16">
            <FileText size={40} className="text-white/10 mb-4" />
            <p className="text-white/40 mb-2">Nessun documento caricato</p>
            <p className="text-white/25 text-sm">Carica PDF, TXT, CSV, MD, JSON o HTML</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {files.map(f => (
            <Card key={f.id} className="bg-[#101013] border-white/[0.08]">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="h-10 w-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                  <FileText size={18} className="text-white/40" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{f.filename}</div>
                  <div className="text-white/30 text-xs">{f.mime_type} · {formatSize(f.size)}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(f.id)}
                  className="h-8 w-8 text-red-400/50 hover:text-red-400 hover:bg-red-400/10">
                  <Trash2 size={14} />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ======== ARTIFACTS TAB (File Explorer) ========
function buildFileTree(artifacts) {
  const root = { name: '/', children: {}, files: [] };
  artifacts.forEach(a => {
    const parts = (a.title || 'file').split('/');
    let current = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const folder = parts[i];
      if (!current.children[folder]) {
        current.children[folder] = { name: folder, children: {}, files: [], path: parts.slice(0, i + 1).join('/') };
      }
      current = current.children[folder];
    }
    current.files.push({ ...a, fileName: parts[parts.length - 1] });
  });
  return root;
}

function FolderNode({ node, depth = 0, selectedId, onSelect, expandedFolders, toggleFolder }) {
  const folders = Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name));
  const files = (node.files || []).sort((a, b) => a.fileName.localeCompare(b.fileName));
  const folderPath = node.path || '/';
  const isExpanded = expandedFolders.has(folderPath);

  return (
    <>
      {folders.map(folder => {
        const fPath = folder.path || folder.name;
        const fExpanded = expandedFolders.has(fPath);
        const itemCount = Object.keys(folder.children).length + (folder.files?.length || 0);
        return (
          <div key={fPath}>
            <button
              onClick={() => toggleFolder(fPath)}
              className="w-full flex items-center gap-1.5 py-1.5 px-2 rounded-md hover:bg-white/[0.04] transition-colors text-left group"
              style={{ paddingLeft: `${depth * 16 + 8}px` }}
            >
              <ChevronRight size={12} className={`text-white/20 transition-transform flex-shrink-0 ${fExpanded ? 'rotate-90' : ''}`} />
              <FolderOpen size={14} className={fExpanded ? 'text-white/50 flex-shrink-0' : 'text-white/30 flex-shrink-0'} />
              <span className="text-xs text-white/60 truncate flex-1">{folder.name}</span>
              <span className="text-[10px] text-white/15">{itemCount}</span>
            </button>
            {fExpanded && (
              <FolderNode node={folder} depth={depth + 1} selectedId={selectedId} onSelect={onSelect}
                expandedFolders={expandedFolders} toggleFolder={toggleFolder} />
            )}
          </div>
        );
      })}
      {files.map(file => (
        <button
          key={file.id}
          onClick={() => onSelect(file)}
          className={`w-full flex items-center gap-1.5 py-1.5 px-2 rounded-md transition-colors text-left ${
            selectedId === file.id ? 'bg-white/[0.08] text-white' : 'hover:bg-white/[0.04] text-white/50'
          }`}
          style={{ paddingLeft: `${depth * 16 + 28}px` }}
        >
          <File size={13} className="text-white/30 flex-shrink-0" />
          <span className="text-xs truncate">{file.fileName}</span>
        </button>
      ))}
    </>
  );
}

function ArtifactsTab({ projectId }) {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [fullContent, setFullContent] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['/']));

  useEffect(() => { loadArtifacts(); }, [projectId]);

  const loadArtifacts = async () => {
    try {
      const data = await api.listArtifacts(projectId);
      setArtifacts(data);
      // Auto-expand first level folders
      const folders = new Set(['/']);
      data.forEach(a => {
        const parts = (a.title || '').split('/');
        if (parts.length > 1) folders.add(parts[0]);
      });
      setExpandedFolders(folders);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const viewArtifact = async (artifact) => {
    try {
      const full = await api.getArtifact(projectId, artifact.id);
      setSelectedArtifact(full);
      setFullContent(full.content);
    } catch (e) { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo file?')) return;
    try {
      await api.deleteArtifact(projectId, id);
      toast.success('File eliminato');
      setSelectedArtifact(null);
      setFullContent(null);
      loadArtifacts();
    } catch (e) { toast.error(e.message); }
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const tree = buildFileTree(artifacts);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/30" size={24} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-white">Artifacts</h2>
          <p className="text-sm text-white/40 mt-1">{artifacts.length} file nel progetto</p>
        </div>
      </div>

      <div className="flex gap-0 rounded-xl overflow-hidden border border-white/[0.08]" style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}>
        {/* File tree */}
        <div className="w-64 sm:w-72 flex-shrink-0 bg-[#0c0c0f] border-r border-white/[0.06] flex flex-col">
          <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between">
            <span className="text-[10px] text-white/25 uppercase tracking-wider font-semibold">Explorer</span>
            <span className="text-[10px] text-white/15">{artifacts.length} file</span>
          </div>
          <ScrollArea className="flex-1 py-1 px-1">
            {artifacts.length === 0 ? (
              <div className="px-3 py-10 text-center">
                <Package size={20} className="mx-auto text-white/10 mb-2" />
                <p className="text-[10px] text-white/20">Nessun file</p>
                <p className="text-[10px] text-white/15 mt-1">Genera file dalla chat o dalla tab Genera App</p>
              </div>
            ) : (
              <FolderNode
                node={tree}
                selectedId={selectedArtifact?.id}
                onSelect={viewArtifact}
                expandedFolders={expandedFolders}
                toggleFolder={toggleFolder}
              />
            )}
          </ScrollArea>
        </div>

        {/* Code viewer */}
        <div className="flex-1 flex flex-col bg-[#0B0B0C] min-w-0">
          {selectedArtifact ? (
            <>
              <div className="h-9 flex items-center px-4 border-b border-white/[0.06] bg-[#0e0e11] gap-2 flex-shrink-0">
                <File size={13} className="text-white/30" />
                <span className="text-xs text-white/60 truncate flex-1">{selectedArtifact.title}</span>
                {selectedArtifact.language && (
                  <Badge variant="outline" className="text-[10px] border-white/10 text-white/30">{selectedArtifact.language}</Badge>
                )}
                <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(fullContent || ''); toast.success('Copiato!'); }}
                  className="h-6 w-6 text-white/20 hover:text-white/50">
                  <Code size={11} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(selectedArtifact.id)}
                  className="h-6 w-6 text-red-400/30 hover:text-red-400 hover:bg-red-400/10">
                  <Trash2 size={11} />
                </Button>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-4">
                  {selectedArtifact.type === 'image' && fullContent?.startsWith('data:') ? (
                    <img src={fullContent} alt={selectedArtifact.title} className="max-w-full rounded-lg" />
                  ) : (
                    <div className="chat-markdown text-sm">
                      <MarkdownRenderer content={'```' + (selectedArtifact.language || '') + '\n' + (fullContent || '') + '\n```'} />
                    </div>
                  )}
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Code size={28} className="mx-auto text-white/8 mb-3" />
                <p className="text-white/20 text-xs">Seleziona un file per visualizzarlo</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ======== GENERATE APP TAB ========
function GenerateAppTab({ projectId, onGenerated }) {
  const [description, setDescription] = useState('');
  const [appType, setAppType] = useState('web');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!description.trim()) return;
    setGenerating(true);
    setResult(null);
    try {
      const data = await api.generateApp(projectId, { description: description.trim(), app_type: appType });
      setResult(data);
      toast.success(`${data.total_files} file generati e salvati come artifact!`);
    } catch (e) {
      toast.error(e.message || 'Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Genera App</h2>
        <p className="text-sm text-white/40 mt-1">Descrivi l'applicazione e IAIA genererà la struttura completa del progetto</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input section */}
        <div className="space-y-4">
          <Card className="bg-[#101013] border-white/[0.08]">
            <CardContent className="p-5 space-y-4">
              <div className="space-y-2">
                <Label className="text-white/60">Tipo di applicazione</Label>
                <Select value={appType} onValueChange={setAppType}>
                  <SelectTrigger className="bg-white/[0.04] border-white/[0.06] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/[0.08]">
                    <SelectItem value="web" className="text-white focus:bg-white/[0.06]">Web App</SelectItem>
                    <SelectItem value="api" className="text-white focus:bg-white/[0.06]">API / Backend</SelectItem>
                    <SelectItem value="fullstack" className="text-white focus:bg-white/[0.06]">Full Stack</SelectItem>
                    <SelectItem value="script" className="text-white focus:bg-white/[0.06]">Script / Tool</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white/60">Descrizione dell'app</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Es. Un'app web per gestire le prenotazioni di un ristorante con calendario, lista clienti e notifiche email..."
                  className="bg-white/[0.04] border-white/[0.06] text-white min-h-[200px] placeholder:text-white/25"
                  data-testid="generate-app-description"
                />
              </div>

              <Button onClick={handleGenerate} disabled={generating || !description.trim()}
                className="w-full bg-white text-black hover:bg-white/90 h-11" data-testid="generate-app-button">
                {generating ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> IAIA sta generando...</>
                ) : (
                  <><Sparkles size={16} className="mr-2" /> Genera progetto</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Result section */}
        <div>
          {generating ? (
            <Card className="bg-[#101013] border-white/[0.08]">
              <CardContent className="flex flex-col items-center py-16">
                <Loader2 size={32} className="animate-spin text-white/30 mb-4" />
                <p className="text-white/50">IAIA sta creando il tuo progetto...</p>
                <p className="text-white/25 text-sm mt-1">Potrebbe richiedere qualche secondo</p>
              </CardContent>
            </Card>
          ) : result ? (
            <Card className="bg-[#101013] border-white/[0.08]">
              <CardHeader>
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Package size={16} /> {result.total_files} file generati
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {result.files?.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                    <Code size={14} className="text-white/40" />
                    <span className="text-sm text-white/70 flex-1 truncate">{f.title}</span>
                    {f.language && <Badge variant="outline" className="text-[10px] border-white/10 text-white/40">{f.language}</Badge>}
                  </div>
                ))}
                <div className="flex gap-2 mt-4">
                  <Button onClick={onGenerated} variant="outline" className="border-white/10 text-white/60 hover:bg-white/[0.04] flex-1">
                    <Package size={14} className="mr-2" /> Vedi Artifacts
                  </Button>
                </div>
                {result.note && <p className="text-white/30 text-xs mt-2">{result.note}</p>}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#101013] border-white/[0.08]">
              <CardContent className="flex flex-col items-center py-16">
                <Sparkles size={40} className="text-white/10 mb-4" />
                <p className="text-white/40 mb-2">Pronto per generare</p>
                <p className="text-white/25 text-sm text-center">Descrivi l'app che vuoi creare e IAIA genererà tutti i file necessari</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


// ======== GIT TAB ========
function GitTab({ projectId }) {
  const navigate = useNavigate();
  const [bbConnected, setBbConnected] = useState(false);
  const [bbUser, setBbUser] = useState(null);
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedRepoName, setSelectedRepoName] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState('');
  const [commitMsg, setCommitMsg] = useState('');
  const [newBranchName, setNewBranchName] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [showNewBranch, setShowNewBranch] = useState(false);
  const [prTitle, setPrTitle] = useState('');
  const [prSource, setPrSource] = useState('');
  const [creatingPR, setCreatingPR] = useState(false);
  const [prs, setPrs] = useState([]);
  const [pushSuccess, setPushSuccess] = useState(false);
  const [projectFiles, setProjectFiles] = useState([]);

  useEffect(() => { checkBitbucket(); loadProjectFiles(); }, []);

  const loadProjectFiles = async () => {
    try {
      const arts = await api.listArtifacts(projectId);
      setProjectFiles(arts.filter(a => a.type === 'app_file' || a.type === 'code'));
    } catch(e) {}
  };

  const checkBitbucket = async () => {
    try {
      const settings = await api.getBitbucketSettings();
      setBbConnected(settings.connected && settings.verified);
      setBbUser(settings);
      if (settings.connected && settings.verified) {
        const data = await api.bbListRepos();
        setRepos(data.repos || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSelectRepo = async (slug) => {
    setSelectedRepo(slug);
    const repo = repos.find(r => r.slug === slug);
    setSelectedRepoName(repo?.name || slug);
    setBranches([]); setSelectedBranch(''); setPrs([]);
    setBranchesLoading(true); setBranchesError(''); setPushSuccess(false);
    try {
      const br = await api.bbListBranches(slug);
      const list = Array.isArray(br) ? br : [];
      setBranches(list);
      if (list.length > 0) setSelectedBranch(list[0].name);
      else setBranchesError('Nessun branch trovato');
    } catch (e) { setBranchesError(e.message || 'Errore'); }
    finally { setBranchesLoading(false); }
    try { const prList = await api.bbListPRs(slug); setPrs(Array.isArray(prList) ? prList : []); } catch(e) {}
  };

  const handleImport = async () => {
    if (!selectedRepo || !selectedBranch) return;
    setImporting(true);
    try { const r = await api.bbImportRepo(selectedRepo, projectId, selectedBranch); toast.success(`${r.imported_files} file importati!`); }
    catch (e) { toast.error(e.message); } finally { setImporting(false); }
  };

  const handlePush = async () => {
    if (!selectedRepo || !selectedBranch || !commitMsg.trim()) return;
    setPushing(true); setPushSuccess(false);
    try {
      const r = await api.bbPushToRepo(selectedRepo, projectId, selectedBranch, commitMsg.trim());
      toast.success(`${r.files_pushed} file pushati su ${selectedBranch}!`);
      setPushSuccess(true); setCommitMsg('');
    } catch (e) { toast.error(e.message); } finally { setPushing(false); }
  };

  const handleCreateBranch = async () => {
    if (!selectedRepo || !newBranchName.trim()) return;
    setCreatingBranch(true);
    try {
      await api.bbCreateBranch(selectedRepo, newBranchName.trim(), selectedBranch || 'main');
      toast.success(`Branch "${newBranchName}" creato!`);
      setShowNewBranch(false); setNewBranchName('');
      const br = await api.bbListBranches(selectedRepo);
      setBranches(Array.isArray(br) ? br : []);
      setSelectedBranch(newBranchName.trim());
    } catch (e) { toast.error(e.message); } finally { setCreatingBranch(false); }
  };

  const handleCreatePR = async () => {
    if (!selectedRepo || !prTitle.trim() || !prSource) return;
    setCreatingPR(true);
    try {
      const r = await api.bbCreatePR(selectedRepo, prTitle.trim(), prSource, 'main', 'Generato da IAIA - TOYOU');
      toast.success('Pull Request creata!'); if (r.url) window.open(r.url, '_blank');
      setPrTitle(''); setPrSource('');
      const prList = await api.bbListPRs(selectedRepo); setPrs(Array.isArray(prList) ? prList : []);
    } catch (e) { toast.error(e.message); } finally { setCreatingPR(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/30" size={24} /></div>;

  if (!bbConnected) return (
    <Card className="bg-[#101013] border-white/[0.08]">
      <CardContent className="flex flex-col items-center py-16">
        <GitBranch size={40} className="text-white/10 mb-4" />
        <p className="text-white/50 mb-2 font-medium">Bitbucket non collegato</p>
        <p className="text-white/25 text-sm mb-6 text-center max-w-md">Per importare e pushare codice, collega il tuo account Bitbucket dalle Impostazioni</p>
        <Button onClick={() => navigate('/settings')} className="bg-white text-black hover:bg-white/90"><GitBranch size={14} className="mr-2" /> Collega Bitbucket</Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><GitBranch size={20} /> Git & Bitbucket</h2>
          <p className="text-sm text-white/40 mt-1">Connesso come <span className="text-white/60">{bbUser?.display_name || bbUser?.username}</span></p>
        </div>
      </div>

      {/* STEP 1 */}
      <Card className="bg-[#101013] border-white/[0.08]">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-7 w-7 rounded-full bg-white text-black flex items-center justify-center text-xs font-bold">1</div>
            <div><h3 className="text-white font-medium text-sm">Seleziona repository</h3><p className="text-white/30 text-xs">Scegli il repo su cui vuoi lavorare</p></div>
          </div>
          <Select value={selectedRepo} onValueChange={handleSelectRepo}>
            <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white h-11"><SelectValue placeholder="Scegli un repository..." /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1e] border-white/[0.08] max-h-60 z-[100]">
              {repos.map(r => (
                <SelectItem key={r.slug} value={r.slug} className="text-white focus:bg-white/[0.08] focus:text-white py-2.5">
                  <div className="flex items-center gap-2"><FolderOpen size={13} className="text-white/30" /><span className="font-medium">{r.name}</span>
                    {r.language && <Badge variant="outline" className="text-[10px] border-white/10 text-white/30 ml-1">{r.language}</Badge>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* STEP 2 */}
      {selectedRepo && (
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-7 w-7 rounded-full bg-white text-black flex items-center justify-center text-xs font-bold">2</div>
              <div><h3 className="text-white font-medium text-sm">Seleziona o crea branch</h3><p className="text-white/30 text-xs">Su quale branch vuoi lavorare?</p></div>
            </div>
            {branchesLoading ? (
              <div className="h-11 bg-white/[0.03] border border-white/[0.06] rounded-lg flex items-center px-4 text-white/30 text-sm"><Loader2 size={14} className="animate-spin mr-2" /> Caricamento branch...</div>
            ) : branchesError ? (
              <div className="h-11 bg-red-500/5 border border-red-500/20 rounded-lg flex items-center px-4 text-red-400/70 text-sm">{branchesError}</div>
            ) : (
              <div className="space-y-3">
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white h-11"><SelectValue placeholder="Seleziona branch..." /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/[0.08] z-[100]">
                    {branches.map(b => (
                      <SelectItem key={b.name} value={b.name} className="text-white focus:bg-white/[0.08] py-2">
                        <div className="flex items-center gap-2"><GitBranch size={12} className="text-white/30" /><span>{b.name}</span><span className="text-white/20 text-[10px] ml-1">{b.hash}</span></div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!showNewBranch ? (
                  <button onClick={() => setShowNewBranch(true)} className="text-xs text-white/30 hover:text-white/60 transition-colors flex items-center gap-1"><Plus size={12} /> Crea nuovo branch da <span className="text-white/50">{selectedBranch || 'main'}</span></button>
                ) : (
                  <div className="flex gap-2 items-center bg-white/[0.02] rounded-lg p-3 border border-white/[0.04]">
                    <GitBranch size={14} className="text-white/20 flex-shrink-0" />
                    <Input value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)} placeholder="feature/nome-branch" className="bg-white/[0.04] border-white/[0.06] text-white h-9 text-sm flex-1" onKeyDown={(e) => { if (e.key === 'Enter') handleCreateBranch(); }} />
                    <Button onClick={handleCreateBranch} disabled={creatingBranch || !newBranchName.trim()} className="bg-white text-black hover:bg-white/90 h-9 px-4 text-xs">{creatingBranch ? <Loader2 size={13} className="animate-spin" /> : 'Crea'}</Button>
                    <button onClick={() => { setShowNewBranch(false); setNewBranchName(''); }} className="text-white/20 hover:text-white/40"><X size={14} /></button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 3: PULL & PUSH */}
      {selectedRepo && selectedBranch && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-[#101013] border-white/[0.08]">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-7 w-7 rounded-full bg-white/10 text-white flex items-center justify-center"><Download size={14} /></div>
                <div><h3 className="text-white font-medium text-sm">Pull (Importa)</h3><p className="text-white/30 text-xs">Scarica i file dal repo nel progetto</p></div>
              </div>
              <p className="text-white/40 text-xs mb-3">Scarica tutti i file da <span className="text-white/60 font-medium">{selectedRepoName}</span> / <span className="text-white/60 font-medium">{selectedBranch}</span> e salvali come file del progetto. Li troverai nella tab <span className="text-white/60">Artifacts</span> e nell'<span className="text-white/60">IDE</span>.</p>
              <Button onClick={handleImport} disabled={importing} variant="outline" className="w-full border-white/10 text-white/60 hover:bg-white/[0.04] h-10">
                {importing ? <><Loader2 size={14} className="animate-spin mr-2" /> Importando...</> : <><Download size={14} className="mr-2" /> Importa file dal repo</>}
              </Button>
            </CardContent>
          </Card>

          <Card className={`bg-[#101013] ${pushSuccess ? 'border-green-500/20' : 'border-white/[0.08]'}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-7 w-7 rounded-full bg-white text-black flex items-center justify-center"><Upload size={14} /></div>
                <div><h3 className="text-white font-medium text-sm">Push (Invia)</h3><p className="text-white/30 text-xs">Invia le modifiche al repo</p></div>
              </div>
              
              {/* File preview */}
              {projectFiles.length > 0 ? (
                <div className="mb-4">
                  <p className="text-white/40 text-xs mb-2">File che verranno pushati su <span className="text-white/60 font-medium">{selectedRepoName}</span> / <span className="text-white/60 font-medium">{selectedBranch}</span>:</p>
                  <div className="bg-white/[0.02] rounded-lg border border-white/[0.04] max-h-40 overflow-auto p-2 space-y-0.5">
                    {projectFiles.map(f => (
                      <div key={f.id} className="flex items-center gap-2 px-2 py-1 text-xs">
                        <Code size={11} className="text-white/25 flex-shrink-0" />
                        <span className="text-white/60 truncate">{f.title}</span>
                        {f.language && <span className="text-white/20 text-[10px]">{f.language}</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-white/25 text-[10px] mt-1">{projectFiles.length} file · Dalla tab Artifacts e IDE del progetto</p>
                </div>
              ) : (
                <div className="mb-4 bg-white/[0.02] rounded-lg border border-white/[0.04] p-4 text-center">
                  <p className="text-white/30 text-xs">Nessun file nel progetto</p>
                  <p className="text-white/20 text-[10px] mt-1">Genera file con IAIA nella tab "Genera App" o importali con Pull</p>
                </div>
              )}

              <div className="space-y-2">
                <Input value={commitMsg} onChange={(e) => { setCommitMsg(e.target.value); setPushSuccess(false); }}
                  placeholder="es. Aggiunto componente header + fix responsive" className="bg-white/[0.04] border-white/[0.08] text-white h-10 text-sm" />
                <Button onClick={handlePush} disabled={pushing || !commitMsg.trim() || projectFiles.length === 0} className="w-full bg-white text-black hover:bg-white/90 h-10 font-medium">
                  {pushing ? <><Loader2 size={14} className="animate-spin mr-2" /> Push in corso...</> : <><Upload size={14} className="mr-2" /> Push {projectFiles.length} file su {selectedBranch}</>}
                </Button>
              </div>
              {pushSuccess && (
                <div className="mt-3 flex items-center gap-2 text-green-400 text-xs bg-green-400/5 rounded-lg px-3 py-2">
                  <CheckCircle size={14} /> Push completato! Ora puoi creare una Pull Request.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* STEP 4: PR */}
      {selectedRepo && selectedBranch && (
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-7 w-7 rounded-full bg-white/10 text-white flex items-center justify-center"><GitPullRequest size={14} /></div>
              <div><h3 className="text-white font-medium text-sm">Pull Request</h3><p className="text-white/30 text-xs">Crea una PR per far revisionare le modifiche</p></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-4">
              <div className="sm:col-span-5 space-y-1">
                <Label className="text-white/40 text-[10px]">Titolo della PR</Label>
                <Input value={prTitle} onChange={(e) => setPrTitle(e.target.value)} placeholder="es. Feature: nuovo header" className="bg-white/[0.04] border-white/[0.06] text-white h-9 text-sm" />
              </div>
              <div className="sm:col-span-3 space-y-1">
                <Label className="text-white/40 text-[10px]">Da branch</Label>
                <Select value={prSource} onValueChange={setPrSource}>
                  <SelectTrigger className="bg-white/[0.04] border-white/[0.06] text-white h-9 text-sm"><SelectValue placeholder="Source..." /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/[0.08] z-[100]">
                    {branches.filter(b => b.name !== 'main').map(b => (<SelectItem key={b.name} value={b.name} className="text-white focus:bg-white/[0.08]">{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-white/40 text-[10px]">Verso</Label>
                <div className="h-9 bg-white/[0.03] border border-white/[0.06] rounded-md flex items-center px-3 text-white/50 text-sm">main</div>
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-white/40 text-[10px]">&nbsp;</Label>
                <Button onClick={handleCreatePR} disabled={creatingPR || !prTitle.trim() || !prSource} className="w-full bg-white text-black hover:bg-white/90 h-9 text-xs font-medium">
                  {creatingPR ? <Loader2 size={13} className="animate-spin" /> : <><GitPullRequest size={13} className="mr-1" /> Crea PR</>}
                </Button>
              </div>
            </div>
            {prs.length > 0 && (
              <>
                <Separator className="bg-white/[0.06] my-4" />
                <Label className="text-white/40 text-[10px]">PR aperte</Label>
                <div className="space-y-1.5 mt-2">
                  {prs.map(pr => (
                    <div key={pr.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                      <GitPullRequest size={14} className="text-green-400/50 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white/70 truncate">{pr.title}</div>
                        <div className="text-[10px] text-white/25">{pr.source_branch} &rarr; {pr.dest_branch} &middot; {pr.author}</div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => window.open(pr.url, '_blank')} className="h-7 text-white/25 hover:text-white/60 text-xs"><ExternalLink size={12} className="mr-1" /> Apri</Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}




// ======== DEPLOY TAB ========
function DeployTab({ projectId, project }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [logs, setLogs] = useState([]);
  const [undeploying, setUndeploying] = useState(false);
  const [infraPreview, setInfraPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [overrideType, setOverrideType] = useState('');
  const [overrideDb, setOverrideDb] = useState('');
  const [overrideStorage, setOverrideStorage] = useState(null);

  const deployStatus = project?.deploy_status || 'not_deployed';
  const isDeployed = deployStatus === 'deployed';

  useEffect(() => { loadStatus(); previewInfra(); }, []);

  const loadStatus = async () => {
    try { const s = await api.getDeployStatus(projectId); setStatus(s); }
    catch (e) {} finally { setLoading(false); }
  };

  const previewInfra = async () => {
    setLoadingPreview(true);
    try {
      const arts = await api.listArtifacts(projectId);
      if (arts.length === 0) { setLoadingPreview(false); return; }
      // Call a lightweight endpoint or do client-side detection
      const filenames = arts.map(a => (a.title || '').toLowerCase());
      const allText = arts.map(a => (a.content_preview || a.title || '').toLowerCase()).join(' ');
      
      let dbType = null;
      if (['mysql', 'pymysql', 'mysql2', 'sequelize'].some(k => allText.includes(k))) dbType = 'mysql';
      else if (['mongoose', 'mongodb', 'pymongo', 'mongo'].some(k => allText.includes(k))) dbType = 'mongodb';
      else if (['postgresql', 'psycopg', 'postgres'].some(k => allText.includes(k))) dbType = 'postgres';
      
      let needsStorage = ['upload', 'multer', 'multipart', 'formidable', 'aiofiles'].some(k => allText.includes(k));
      
      let projType = 'static';
      if (filenames.some(f => f === 'package.json')) projType = 'node';
      if (filenames.some(f => f === 'requirements.txt' || f.endsWith('.py'))) projType = 'python';
      
      setInfraPreview({ db_type: dbType, needs_storage: needsStorage, project_type: projType, file_count: arts.length });
    } catch(e) {}
    finally { setLoadingPreview(false); }
  };

  const addLog = (text, type = 'info') => {
    setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), text, type }]);
  };

  const handleDeploy = async () => {
    setDeploying(true);
    setLogs([]);
    addLog('Avvio pipeline di deploy...');
    addLog('Tipo progetto: auto-detect dai file');
    if (infraPreview?.db_type) addLog(`Database rilevato: ${infraPreview.db_type}`);
    
    try {
      addLog('Analisi codice e rilevamento infrastruttura...');
      if (infraPreview?.db_type) addLog(`Database rilevato: ${infraPreview.db_type}`);
      if (infraPreview?.needs_storage) addLog('Storage per uploads rilevato');
      addLog(`Tipo progetto: ${infraPreview?.project_type || 'auto-detect'}`);
      addLog('Build immagine Docker con BuildKit...');
      const deployOptions = {};
      if (overrideDb) deployOptions.db_type = overrideDb === 'none' ? null : overrideDb;
      if (overrideStorage !== null) deployOptions.needs_storage = overrideStorage;
      const result = await api.deployProject(projectId, deployOptions);
      
      result.steps?.forEach(s => {
        const msgs = {
          'namespace': 'Namespace K8s creato',
          'harbor_secret': 'Secret Harbor configurato',
          'kaniko_job': s.status === 'ok' ? `Build job avviato: ${s.image}` : `Build job fallito: ${s.error}`,
          'build_wait': s.status === 'ok' ? 'Build completata!' : `Build fallita: ${s.error}`,
          'deploy': s.status === 'ok' ? `App deployata su ${s.hostname}` : 'Deploy fallito',
        };
        addLog(msgs[s.step] || `${s.step}: ${s.status}`, s.status === 'ok' ? 'success' : s.status === 'error' ? 'error' : 'info');
      });
      
      if (result.success) {
        addLog(`Deploy completato! URL: https://${result.hostname}`, 'success');
      } else {
        addLog('Deploy fallito. Controlla i log.', 'error');
      }
      
      setTimeout(loadStatus, 3000);
      window.location.reload();
    } catch (e) {
      addLog(`Errore: ${e.message}`, 'error');
    } finally { setDeploying(false); }
  };

  const handleUndeploy = async () => {
    if (!window.confirm('Rimuovere il deploy? L\'app non sarà più accessibile.')) return;
    setUndeploying(true);
    try {
      await api.undeployProject(projectId);
      toast.success('Deploy rimosso');
      window.location.reload();
    } catch (e) { toast.error(e.message); }
    finally { setUndeploying(false); }
  };

  const k8s = status?.k8s?.deployment || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2"><Rocket size={20} /> Deploy</h2>
        <p className="text-sm text-white/40 mt-1">Deploya {project?.name} su K8s con un click</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/30" size={24} /></div>
      ) : isDeployed ? (
        <>
          {/* Deployed state */}
          <Card className="bg-[#101013] border-green-500/20">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                    <h3 className="text-white font-semibold text-lg">Live</h3>
                    <Badge className="bg-green-500/10 text-green-400 text-xs">{k8s.status || 'running'}</Badge>
                  </div>
                  <a href={`https://${status?.deploy_hostname}`} target="_blank" rel="noreferrer"
                    className="text-white/70 hover:text-white text-sm flex items-center gap-1 mb-4 underline underline-offset-2">
                    https://{status?.deploy_hostname} <ExternalLink size={12} />
                  </a>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-xs">
                    <div><span className="text-white/30 block">Tipo</span><span className="text-white/60 capitalize">{status?.deploy_type || '—'}</span></div>
                    <div><span className="text-white/30 block">Repliche</span><span className="text-white/60">{k8s.ready || 0}/{k8s.replicas || 0}</span></div>
                    <div><span className="text-white/30 block">Database</span><span className="text-white/60">{status?.deploy_infra?.db_type || status?.deploy_db?.type || 'Nessuno'}</span></div>
                    <div><span className="text-white/30 block">Storage</span><span className="text-white/60">{status?.deploy_infra?.needs_storage ? 'Sì' : 'No'}</span></div>
                    <div><span className="text-white/30 block">Namespace</span><span className="text-white/60">{status?.deploy_namespace || '—'}</span></div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleDeploy} disabled={deploying} className="bg-white text-black hover:bg-white/90">
                    {deploying ? <Loader2 size={14} className="animate-spin mr-2" /> : <Rocket size={14} className="mr-2" />} Re-deploy
                  </Button>
                  <Button onClick={handleUndeploy} disabled={undeploying} variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10">
                    {undeploying ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Not deployed - deploy form */}
          <Card className="bg-[#101013] border-white/[0.08]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
                  <Rocket size={24} className="text-white/40" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Pronto per il deploy</h3>
                  <p className="text-white/30 text-sm">IAIA analizzerà il tuo codice, builderà un'immagine Docker e la deployerà su K8s</p>
                </div>
              </div>

              <div className="bg-white/[0.02] rounded-lg p-4 mb-4 space-y-4">
                <h4 className="text-white/60 text-xs font-medium uppercase tracking-wider">Infrastruttura rilevata dal codice</h4>
                {loadingPreview ? (
                  <div className="flex items-center gap-2 text-white/30 text-sm"><Loader2 size={14} className="animate-spin" /> Analisi file...</div>
                ) : infraPreview ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Project type */}
                      <div className="space-y-1.5">
                        <span className="text-white/30 text-[10px] uppercase">Tipo progetto</span>
                        <Select value={overrideType || infraPreview.project_type} onValueChange={setOverrideType}>
                          <SelectTrigger className="bg-white/[0.03] border-white/[0.06] text-white h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1e] border-white/[0.08] z-[100]">
                            <SelectItem value="static" className="text-white focus:bg-white/[0.08]">Static (HTML/CSS/JS)</SelectItem>
                            <SelectItem value="node" className="text-white focus:bg-white/[0.08]">Node.js</SelectItem>
                            <SelectItem value="python" className="text-white focus:bg-white/[0.08]">Python</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Database */}
                      <div className="space-y-1.5">
                        <span className="text-white/30 text-[10px] uppercase">Database</span>
                        <Select value={overrideDb || infraPreview.db_type || 'none'} onValueChange={setOverrideDb}>
                          <SelectTrigger className="bg-white/[0.03] border-white/[0.06] text-white h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1e] border-white/[0.08] z-[100]">
                            <SelectItem value="none" className="text-white focus:bg-white/[0.08]">Nessuno</SelectItem>
                            <SelectItem value="mongodb" className="text-white focus:bg-white/[0.08]">MongoDB</SelectItem>
                            <SelectItem value="mysql" className="text-white focus:bg-white/[0.08]">MySQL</SelectItem>
                            <SelectItem value="postgres" className="text-white focus:bg-white/[0.08]">PostgreSQL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Storage */}
                      <div className="space-y-1.5">
                        <span className="text-white/30 text-[10px] uppercase">Storage uploads</span>
                        <Select value={String(overrideStorage !== null ? overrideStorage : infraPreview.needs_storage)} onValueChange={(v) => setOverrideStorage(v === 'true')}>
                          <SelectTrigger className="bg-white/[0.03] border-white/[0.06] text-white h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1e] border-white/[0.08] z-[100]">
                            <SelectItem value="false" className="text-white focus:bg-white/[0.08]">No</SelectItem>
                            <SelectItem value="true" className="text-white focus:bg-white/[0.08]">Sì (2Gi Longhorn)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Files count */}
                      <div className="space-y-1.5">
                        <span className="text-white/30 text-[10px] uppercase">File nel progetto</span>
                        <div className="h-9 bg-white/[0.03] border border-white/[0.06] rounded-md flex items-center px-3 text-white/60 text-sm">
                          {infraPreview.file_count}
                        </div>
                      </div>
                    </div>
                    <p className="text-white/20 text-[10px]">I valori sono auto-rilevati dal codice. Puoi modificarli prima del deploy.</p>
                  </>
                ) : (
                  <p className="text-white/25 text-xs">Nessun file nel progetto</p>
                )}
                <Separator className="bg-white/[0.06]" />
                <div className="text-xs text-white/25">
                  <div>L'app sarà accessibile su <span className="text-white/50">{(project?.name || 'app').toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').slice(0, 50)}.apps.toyou.it</span></div>
                </div>
              </div>

              <Button onClick={handleDeploy} disabled={deploying} className="w-full bg-white text-black hover:bg-white/90 h-12 font-semibold text-base">
                {deploying ? <><Loader2 size={16} className="animate-spin mr-2" /> Deploy in corso (può richiedere 2-3 minuti)...</> : <><Rocket size={16} className="mr-2" /> Deploy {project?.name}</>}
              </Button>

              {deployStatus === 'build_failed' && (
                <div className="mt-3 text-red-400 text-xs bg-red-400/5 rounded-lg px-3 py-2">
                  L'ultimo deploy è fallito. Controlla i file del progetto e riprova.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Deploy logs */}
      {logs.length > 0 && (
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardContent className="p-4">
            <span className="text-white/40 text-xs uppercase tracking-wider font-medium">Pipeline log</span>
            <div className="space-y-1 mt-3 font-mono">
              {logs.map((log, i) => (
                <div key={i} className={`text-xs flex items-start gap-2 ${log.type === 'error' ? 'text-red-400/80' : log.type === 'success' ? 'text-green-400/70' : 'text-white/40'}`}>
                  <span className="text-white/15 flex-shrink-0">{log.time}</span>
                  {log.type === 'success' && <CheckCircle size={12} className="flex-shrink-0 mt-0.5" />}
                  <span>{log.text}</span>
                </div>
              ))}
              {deploying && <div className="text-white/30 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> In corso...</div>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

